/**
 * Bucket түлхүүрийг монгол шошго болгоно.
 *
 * Түлхүүрийн формат нь сервертэй нэг (`@boots/domain/revenue.ts`):
 *   `2026-08-24T14` · `2026-08-24` · `2026-08`
 *
 * ⚠️ `new Date(key)` руу хөрвүүлэхгүй — түлхүүр нь аль хэдийн УБ-ийн
 *    орон нутгийн цаг. `Date` болговол хөтөч түүнийг UTC гэж уншаад
 *    дахин хөрвүүлж 8 цагаар зөрүүлнэ.
 */

import { WEEKDAY_MN, type RevenueRange } from '@boots/domain';

/**
 * Тэнхлэг дээр ХОЁР үсэг — 30 цэгтэй муруйн доор бүтэн нэр багтахгүй.
 * Тайлбарын цонхонд БҮТЭН нэр: тэнд зай бий, «Ня» гэж товчлох
 * шалтгаангүй (2026-09-25, эзэмшигчийн хариу).
 *
 * ⚠️ Нэрийг энд ДАХИН бичихгүй — `@boots/domain`-ий `WEEKDAY_MN`-ээс
 *    гаргана: хоёр газар бичвэл нэг нь өөрчлөгдөхөд нөгөө нь хоцорно.
 */
const WEEKDAY_SHORT = WEEKDAY_MN.map((d) => d.slice(0, 2));
const MONTH_SHORT = [
  '1-р', '2-р', '3-р', '4-р', '5-р', '6-р',
  '7-р', '8-р', '9-р', '10-р', '11-р', '12-р',
];

/** Түлхүүрээс тоонуудыг салгана — Date ашиглахгүй. */
function parts(key: string): { y: number; m: number; d: number; h: number } {
  const [date, hour] = key.split('T');
  const [y, m, d] = (date ?? '').split('-').map(Number);
  return { y: Number(y), m: Number(m), d: Number(d ?? 1), h: Number(hour ?? 0) };
}

/** Гаригийг UTC дээр тооцно — орон нутгийн цаг нөлөөлөхгүй. */
function weekday(y: number, m: number, d: number): number {
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function bucketLabel(
  range: RevenueRange,
  key: string,
  form: 'short' | 'long',
): string {
  const p = parts(key);

  if (range === 'day') {
    return form === 'short'
      ? `${p.h}:00`
      : `${p.y} оны ${MONTH_SHORT[p.m - 1]} сарын ${p.d}, ${String(p.h).padStart(2, '0')}:00`;
  }

  if (range === 'year') {
    return form === 'short'
      ? MONTH_SHORT[p.m - 1] ?? String(p.m)
      : `${p.y} оны ${MONTH_SHORT[p.m - 1]} сар`;
  }

  if (range === 'week') {
    return form === 'short'
      ? WEEKDAY_SHORT[weekday(p.y, p.m, p.d)] ?? String(p.d)
      : `${WEEKDAY_MN[weekday(p.y, p.m, p.d)]} гараг · ${MONTH_SHORT[p.m - 1]} сарын ${p.d}`;
  }

  // month — 1..31
  return form === 'short' ? String(p.d) : `${MONTH_SHORT[p.m - 1]} сарын ${p.d}`;
}

/**
 * Хэдэн шошго тутамд нэгийг харуулах вэ.
 *
 * 375px дээр 24 цаг эсвэл 31 өдрийг бүгдийг нь бичвэл давхцана.
 * Тиймээс ойролцоогоор 7-8 шошго багтаахаар цөөрүүлнэ.
 */
export function labelStepFor(range: RevenueRange, count: number): number {
  if (range === 'week') return 1; // 7 ширхэг — бүгд багтана
  // 12 ширхэг. 375px-ээс дээш багтана; 320px дээр `revenue-chart.tsx`
  // тэдгээрийн нэг дэх бүрийг CSS-ээр (`hidden sm:inline`) нууна.
  if (range === 'year') return 1;
  return Math.max(1, Math.ceil(count / 8));
}
